import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Check, Pause, Play } from "lucide-react";
import { VOICES } from "../shared/catalog";
import type { Voice } from "../shared/types";
import SAMPLES from "../shared/voice-samples.json";
import { Mark, Modal } from "./components";
import { bilingual, text, useLang, useText, type Lang } from "./i18n";

const TEXT = bilingual({
  en: {
    picker: {
      title: "Voice",
      show: "Voices to show",
      all: "All",
      female: "Female",
      male: "Male",
      sampleLanguage: "Sample language",
      spanish: "Spanish samples",
      english: "English samples",
      narrator: "Narrator",
      auto: "Director's choice",
      matched: "Matched to each video",
      use: (name: string) => `Use ${name}`,
      letChoose: "Let the Director choose",
    },
    card: {
      female: "Female",
      male: "Male",
      play: (name: string) => `Play ${name}`,
      pause: (name: string) => `Pause ${name}`,
    },
    // How each narrator sounds, by voice ID; voices.json holds the English.
    characters: {} as Record<string, string>,
  },
  es: {
    picker: {
      title: "Voz",
      show: "Voces que mostrar",
      all: "Todas",
      female: "Femeninas",
      male: "Masculinas",
      sampleLanguage: "Idioma de las muestras",
      spanish: "Muestras en español",
      english: "Muestras en inglés",
      narrator: "Narrador",
      auto: "Decide el director",
      matched: "Adaptada a cada vídeo",
      use: (name: string) => `Usar a ${name}`,
      letChoose: "Que elija el director",
    },
    card: {
      female: "Femenina",
      male: "Masculina",
      play: (name: string) => `Reproducir ${name}`,
      pause: (name: string) => `Pausar ${name}`,
    },
    characters: {
      Sulafat: "Cálida",
      Erinome: "Clara",
      Despina: "Suave",
      Callirrhoe: "Relajada",
      Achird: "Cercano",
      Orus: "Firme",
      Puck: "Animado",
      Rasalgethi: "Informativo",
      Zubenelgenubi: "Desenfadado",
      Fenrir: "Enérgico",
    },
  },
});

type Gender = "all" | Voice["gender"];
const PEAKS = SAMPLES as Record<string, { seconds: number; peaks: number[] }>;

/** One sample at a time, with its playback position for the waveform and ring. */
function useSample(language: Lang) {
  const audio = useRef<HTMLAudioElement | null>(null),
    frame = useRef(0);
  const [state, setState] = useState({ id: "", playing: false, progress: 0 });
  const stop = () => {
    cancelAnimationFrame(frame.current);
    audio.current?.pause();
    setState({ id: "", playing: false, progress: 0 });
  };
  useEffect(() => stop, [language]);
  const play = (id: string, restart = false) => {
    const el = (audio.current ??= new Audio());
    cancelAnimationFrame(frame.current);
    if (state.id === id && state.playing && !restart) {
      el.pause();
      setState((s) => ({ ...s, playing: false }));
      return;
    }
    if (state.id !== id || restart) {
      el.src = `/voices/${id}-${language}.mp3`;
      el.currentTime = 0;
    }
    const tick = () => {
      setState({
        id,
        playing: !el.paused,
        progress: el.duration ? el.currentTime / el.duration : 0,
      });
      if (!el.paused) frame.current = requestAnimationFrame(tick);
    };
    el.onended = () => {
      cancelAnimationFrame(frame.current);
      setState({ id, playing: false, progress: 0 });
    };
    void el
      .play()
      .then(tick, () => setState({ id, playing: false, progress: 0 }));
    setState((s) => ({
      id,
      playing: true,
      progress: s.id === id ? s.progress : 0,
    }));
  };
  return { ...state, play, stop };
}

export function voiceName(id: string | null) {
  return VOICES.find((v) => v.id === id)?.name ?? null;
}
/** How a narrator sounds, in the visitor's language. */
export function voiceCharacter(id: string | null) {
  const voice = VOICES.find((v) => v.id === id);
  return voice ? (text(TEXT).characters[voice.id] ?? voice.character) : null;
}

/** Choose the narrator from the studio's voices, listening before choosing. */
export function VoicePicker({
  value,
  onChoose,
  onClose,
}: {
  value: string | null;
  onChoose: (id: string | null) => void;
  onClose: () => void;
}) {
  const t = useText(TEXT).picker,
    site = useLang();
  // Samples play in the site's language; the switch here tries the other.
  const [choice, setChoice] = useState(value),
    [gender, setGender] = useState<Gender>("all"),
    [language, setLanguage] = useState<Lang>(site);
  useEffect(() => setLanguage(site), [site]);
  const sample = useSample(language);
  const visible = VOICES.filter((v) => gender === "all" || v.gender === gender);
  const pick = (id: string | null) => {
    setChoice(id);
    if (id) sample.play(id, sample.id !== id || !sample.playing);
    else sample.stop();
  };
  return (
    <Modal title={t.title} onClose={onClose} className="voice-modal">
      <div className="voice-tools">
        <div className="seg" role="group" aria-label={t.show}>
          {(
            [
              ["all", t.all],
              ["female", t.female],
              ["male", t.male],
            ] as const
          ).map(([g, label]) => (
            <button
              key={g}
              type="button"
              aria-pressed={gender === g}
              onClick={() => setGender(g)}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          className="seg voice-lang"
          role="group"
          aria-label={t.sampleLanguage}
        >
          {(
            [
              ["es", "ES"],
              ["en", "EN"],
            ] as const
          ).map(([l, label]) => (
            <button
              key={l}
              type="button"
              aria-pressed={language === l}
              aria-label={l === "es" ? t.spanish : t.english}
              onClick={() => setLanguage(l)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="voice-grid" role="radiogroup" aria-label={t.narrator}>
        <div className={`voice-card director ${choice === null ? "on" : ""}`}>
          <button
            type="button"
            role="radio"
            aria-checked={choice === null}
            className="voice-pick"
            onClick={() => pick(null)}
          >
            <span className="voice-face">
              <Mark converged />
            </span>
            <span className="voice-name">{t.auto}</span>
            <span className="voice-meta">{t.matched}</span>
          </button>
          {choice === null && <Check size={15} className="voice-check" />}
        </div>
        {visible.map((voice) => (
          <VoiceCard
            key={voice.id}
            voice={voice}
            language={language}
            chosen={choice === voice.id}
            playing={sample.id === voice.id && sample.playing}
            progress={sample.id === voice.id ? sample.progress : 0}
            onPick={() => pick(voice.id)}
            onPlay={() => sample.play(voice.id)}
          />
        ))}
      </div>
      <div className="voice-foot">
        <button
          type="button"
          className="button primary large"
          onClick={() => {
            sample.stop();
            onChoose(choice);
            onClose();
          }}
        >
          {choice ? t.use(voiceName(choice) ?? "") : t.letChoose}
        </button>
      </div>
    </Modal>
  );
}

function VoiceCard({
  voice,
  language,
  chosen,
  playing,
  progress,
  onPick,
  onPlay,
}: {
  voice: Voice;
  language: Lang;
  chosen: boolean;
  playing: boolean;
  progress: number;
  onPick: () => void;
  onPlay: () => void;
}) {
  const t = useText(TEXT).card;
  const peaks = PEAKS[`${voice.id}-${language}`]?.peaks ?? [];
  return (
    <div
      className={`voice-card ${chosen ? "on" : ""} ${playing ? "is-playing" : ""}`}
      style={{ "--p": progress } as CSSProperties}
    >
      <button
        type="button"
        role="radio"
        aria-checked={chosen}
        className="voice-pick"
        onClick={onPick}
      >
        <span className="voice-face" aria-hidden="true" />
        <span className="voice-name">{voice.name}</span>
        <span className="voice-meta">
          {voiceCharacter(voice.id)} ·{" "}
          {voice.gender === "female" ? t.female : t.male}
        </span>
        <span className="voice-wave" aria-hidden="true">
          {peaks.map((h, i) => (
            <i
              key={i}
              className={i / peaks.length < progress ? "played" : ""}
              style={{ "--h": Math.max(0.12, h) } as CSSProperties}
            />
          ))}
        </span>
      </button>
      <button
        type="button"
        className="voice-play"
        aria-label={playing ? t.pause(voice.name) : t.play(voice.name)}
        onClick={onPlay}
      >
        <svg viewBox="0 0 40 40" aria-hidden="true">
          <circle cx="20" cy="20" r="18.5" pathLength="1" />
        </svg>
        {playing ? <Pause size={15} /> : <Play size={15} />}
      </button>
      {chosen && <Check size={15} className="voice-check" />}
    </div>
  );
}

/** The composer's voice control: the chosen narrator, opening the picker. */
